<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useNow } from '@vueuse/core';
import Icon from '../icon/Icon.vue';
import Button from '../button/Button.vue';
import ComboBox from '../combobox/ComboBox.vue';
import Input from '../Input/Input.vue';
import Label from 'dashboard/components/ui/Label.vue';
import FunnelsAPI from 'dashboard/api/funnels';
import CrmAPI from 'dashboard/api/crm';
import CreateFunnelDialog from '../Contacts/Kanban/CreateFunnelDialog.vue';
import CrmStageSettings from './CrmStageSettings.vue';
import CrmPanel from './CrmPanel.vue';
import CrmLoadingSkeleton from './CrmLoadingSkeleton.vue';

const store = useStore();
const { locale, t } = useI18n();
const now = useNow({ interval: 60000 });
const accountLabels = computed(() => store.getters['labels/getLabels'] || []);
const labelColor = title =>
  accountLabels.value.find(label => label.title === title)?.color;
const contactName = item =>
  /[\p{L}\p{N}]/u.test(item.name || '') ? item.name : item.phone_number || '—';
const initials = item =>
  (item.name || '')
    .match(/\p{L}+/gu)
    ?.slice(0, 2)
    .map(word => word[0])
    .join('')
    .toLocaleUpperCase() || '';
const language = computed(() => locale.value.replace('_', '-'));
const compactDate = value =>
  new Date(value).toLocaleString(language.value, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
const relativeDate = value => {
  const minutes = Math.max(
    0,
    Math.floor((now.value - new Date(value)) / 60000)
  );
  let unit = 'day';
  if (minutes < 1440) unit = 'hour';
  if (minutes < 60) unit = 'minute';
  const amount =
    unit === 'minute'
      ? minutes
      : Math.floor(minutes / (unit === 'hour' ? 60 : 1440));
  return new Intl.RelativeTimeFormat(language.value, {
    numeric: 'auto',
    style: 'short',
  }).format(-amount, unit);
};
const overdue = item =>
  item.workflow.state.status !== 'closed' &&
  new Date(item.workflow.deadline_at) < now.value;
const route = useRoute();
const router = useRouter();
const funnels = ref([]);
const flowOptions = computed(() =>
  funnels.value.map(item => ({ value: String(item.id), label: item.name }))
);
const selected = ref('');
const items = ref([]);
const stageColumns = ref([]);
const stagePages = ref({});
const administrator = ref(false);
const creating = ref(false);
const details = ref(null);
const configuring = ref(false);
const settingsPanel = ref(null);
const settingsTrigger = ref(null);
const closeSettings = async () => {
  configuring.value = false;
  await nextTick();
  settingsTrigger.value?.focus();
};
const openSettings = async event => {
  settingsTrigger.value = event.currentTarget;
  details.value = null;
  configuring.value = true;
  await nextTick();
  settingsPanel.value?.focus();
};
const showContacts = ref(false);
const page = ref(1);
const total = ref(0);
const query = ref('');
const busy = ref(false);
const initializing = ref(true);
const loading = computed(() => initializing.value || busy.value);
const contactQuery = ref('');
const contactSort = ref('recent');
const detailTrigger = ref(null);
const closeDetails = async event => {
  if (event && document.querySelector('dialog[open]')) return;
  details.value = null;
  await nextTick();
  detailTrigger.value?.focus();
};
const detailPanel = ref(null);
const requestedStage = ref('');
const dragging = ref(null);
const dropTarget = ref('');
const sortOptions = computed(() =>
  ['recent', 'oldest', 'name'].map(value => ({
    value,
    label: t(`CRM.board.${value}`),
  }))
);
const canDrag = item =>
  !busy.value &&
  item.can_edit &&
  item.conversation_id &&
  item.workflow?.state.status === 'active';
const startDrag = (event, item) => {
  dragging.value = item;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', String(item.id));
};
const endDrag = () => {
  dragging.value = null;
  dropTarget.value = '';
};
const closed = ref(false);
const error = ref('');
let generation = 0;
const funnel = computed(() =>
  funnels.value.find(f => f.id === Number(selected.value))
);
const stageData = column => stageColumns.value.find(c => c.id === column.id);
const cards = column => stageData(column)?.items.filter(i => i.workflow) || [];
const date = value => new Date(value).toLocaleString();
const load = async () => {
  if (!selected.value) return;
  generation += 1;
  const request = generation;
  busy.value = true;
  error.value = '';
  try {
    const { data } = await CrmAPI.board({
      funnel_id: selected.value,
      page: page.value,
      query: query.value,
      contact_query: contactQuery.value,
      contact_sort: contactSort.value,
      closed: closed.value,
      stage_pages: stagePages.value,
    });
    if (request !== generation) return;
    items.value = data.items;
    stageColumns.value = data.columns;
    administrator.value = data.administrator;
    total.value = data.total;
  } catch (e) {
    if (request === generation)
      error.value = e.response?.data?.error || e.message;
  } finally {
    if (request === generation) busy.value = false;
  }
};
const openConversation = item =>
  router.push({
    path: item.conversation_id
      ? `/app/accounts/${route.params.accountId}/conversations/${item.conversation_id}`
      : `/app/accounts/${route.params.accountId}/contacts/${item.id}`,
    query: { crm_funnel: selected.value },
  });
const open = async (item, destination = '', trigger = null) => {
  configuring.value = false;
  detailTrigger.value = trigger;
  details.value = null;
  requestedStage.value = '';
  await nextTick();
  details.value = item;
  requestedStage.value = destination;
  await nextTick();
  detailPanel.value?.focus();
};
const drop = column => {
  const item = dragging.value;
  endDrag();
  if (item && item.workflow.column_id !== column.id) open(item, column.id);
};
const searchContacts = () => {
  if (page.value === 1) load();
  else page.value = 1;
};
watch(contactSort, searchContacts);
watch([page, closed], load);
watch(selected, () => {
  page.value = 1;
  stagePages.value = {};
  configuring.value = false;
  details.value = null;
  load();
});
const movePage = (column, direction) => {
  stagePages.value[column.id] = (stagePages.value[column.id] || 1) + direction;
  load();
};
const created = async () => {
  funnels.value = (await FunnelsAPI.get()).data;
  selected.value = String(funnels.value.at(-1).id);
};
const configured = async () => {
  funnels.value = (await FunnelsAPI.get()).data;
  await closeSettings();
  load();
};
onMounted(async () => {
  store.dispatch('labels/get');
  try {
    const { data } = await FunnelsAPI.get();
    funnels.value = data;
    selected.value = String(data[0]?.id || '');
    await nextTick();
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  } finally {
    initializing.value = false;
  }
});
</script>

<template>
  <div
    class="relative flex h-full min-w-0 flex-1 flex-col bg-n-background text-n-slate-12"
  >
    <header
      class="flex flex-wrap items-center gap-2 border-b border-n-weak bg-n-solid-2 px-4 py-3"
    >
      <h1 class="m-0 mr-2 text-base font-semibold">
        {{ $t('CRM.board.title') }}
      </h1>
      <ComboBox
        v-model="selected"
        :options="flowOptions"
        :allow-deselect="false"
        :aria-label="$t('CRM.flow')"
        class="!w-44"
      />
      <Button
        v-if="administrator"
        v-tooltip="$t('CRM.newFlow')"
        :aria-label="$t('CRM.newFlow')"
        :title="$t('CRM.newFlow')"
        icon="i-lucide-plus"
        slate
        outline
        @click="creating = true"
      />
      <Button
        v-if="administrator && funnel"
        v-tooltip="$t('CRM.configure')"
        :aria-label="$t('CRM.configure')"
        :title="$t('CRM.configure')"
        :aria-pressed="configuring"
        icon="i-lucide-settings-2"
        slate
        outline
        @click="openSettings"
      />
      <form
        class="flex min-w-0 basis-full items-center gap-1 md:ml-auto md:basis-auto"
        @submit.prevent="
          stagePages = {};
          load();
        "
      >
        <Input
          v-model="query"
          :placeholder="$t('CRM.board.searchPipeline')"
          :aria-label="$t('CRM.search')"
          class="min-w-0 flex-1 md:w-56"
        />
        <Button
          v-tooltip="$t('CRM.search')"
          type="submit"
          :aria-label="$t('CRM.search')"
          :title="$t('CRM.search')"
          icon="i-lucide-search"
          slate
          outline
        />
        <Button
          v-tooltip="$t('CRM.contacts')"
          type="button"
          :aria-label="$t('CRM.contacts')"
          :title="$t('CRM.contacts')"
          :aria-pressed="showContacts"
          icon="i-lucide-users"
          slate
          outline
          class="lg:hidden"
          @click="showContacts = !showContacts"
        />
        <Button
          v-tooltip="$t('CRM.showClosed')"
          type="button"
          :aria-label="$t('CRM.showClosed')"
          :title="$t('CRM.showClosed')"
          :aria-pressed="closed"
          icon="i-lucide-archive"
          :color="closed ? 'blue' : 'slate'"
          :variant="closed ? 'faded' : 'outline'"
          @click="closed = !closed"
        />
        <Button
          v-tooltip="$t('CRM.refresh')"
          type="button"
          :disabled="busy"
          :is-loading="busy"
          :aria-label="$t('CRM.refresh')"
          :title="$t('CRM.refresh')"
          icon="i-lucide-refresh-cw"
          slate
          outline
          @click="load"
        />
      </form>
    </header>
    <p v-if="error" role="alert" class="m-3 text-n-ruby-11">{{ error }}</p>
    <p v-if="loading" role="status" class="sr-only">{{ $t('CRM.loading') }}</p>
    <CreateFunnelDialog v-model:show="creating" @create="created" />
    <div class="flex min-h-0 flex-1 gap-3 overflow-hidden bg-n-background p-3">
      <aside
        :class="showContacts ? 'flex' : 'hidden'"
        class="w-full shrink-0 flex-col overflow-hidden rounded-xl border border-n-strong bg-n-solid-2 lg:flex lg:w-72"
      >
        <div
          class="sticky top-0 z-10 border-b border-n-weak bg-n-solid-2 px-4 py-3"
        >
          <h3 class="m-0 flex items-center gap-2 text-sm font-semibold">
            <span
              aria-hidden="true"
              class="i-lucide-users size-4 text-n-slate-11"
            />{{ $t('CRM.contacts') }}
          </h3>
          <form
            class="mt-3 flex items-center gap-1"
            @submit.prevent="searchContacts"
          >
            <Input
              v-model="contactQuery"
              :placeholder="$t('CRM.board.searchContacts')"
              :aria-label="$t('CRM.board.searchContacts')"
              class="min-w-0 flex-1"
            />
            <Button
              type="submit"
              icon="i-lucide-search"
              :aria-label="$t('CRM.search')"
              :title="$t('CRM.search')"
              slate
              outline
            />
          </form>
          <label class="mb-1 mt-3 block text-xs text-n-slate-11">{{
            $t('CRM.board.sort')
          }}</label>
          <ComboBox
            v-model="contactSort"
            :options="sortOptions"
            :allow-deselect="false"
            :aria-label="$t('CRM.board.sort')"
          />
        </div>
        <div :aria-busy="loading" class="min-h-0 flex-1 overflow-y-auto">
          <CrmLoadingSkeleton v-if="loading" contacts />
          <template v-else>
            <button
              v-for="item in items"
              :key="item.id"
              class="flex w-full items-center gap-3 border-b border-n-weak px-4 py-3 text-left hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-brand"
              :class="{ 'bg-n-alpha-2': details?.id === item.id }"
              @click="open(item, '', $event.currentTarget)"
            >
              <span
                aria-hidden="true"
                class="flex size-9 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-xs font-medium text-n-slate-11"
                >{{ initials(item) }}
                <Icon
                  v-if="!initials(item)"
                  icon="i-lucide-user-round"
                  class="size-4"
              /></span>
              <span class="flex min-w-0 flex-1 flex-col gap-1">
                <span class="flex min-w-0 items-baseline gap-2"
                  ><strong
                    class="min-w-0 flex-1 truncate text-sm font-medium"
                    :title="contactName(item)"
                    >{{ contactName(item) }}</strong
                  ><time
                    v-if="item.last_interaction"
                    :datetime="item.last_interaction"
                    :title="date(item.last_interaction)"
                    class="shrink-0 text-xs text-n-slate-11"
                    >{{ relativeDate(item.last_interaction) }}</time
                  ></span
                >
                <span class="truncate text-xs text-n-slate-11">{{
                  item.last_message || item.phone_number
                }}</span>
              </span>
            </button>
            <p v-if="!items.length" class="p-4 text-sm text-n-slate-11">
              {{ $t('CRM.board.emptyContacts') }}
            </p>
          </template>
        </div>
        <footer
          class="flex items-center justify-between gap-2 border-t border-n-weak px-3 py-2 text-xs text-n-slate-11"
        >
          <span
            v-if="loading"
            aria-hidden="true"
            class="h-3 w-32 rounded bg-n-slate-3 dark:bg-n-slate-5"
          />
          <span v-else>{{
            $t('CRM.board.pagination', {
              page,
              pages: Math.max(1, Math.ceil(total / 30)),
              total,
            })
          }}</span>
          <Button
            :disabled="page <= 1 || busy"
            :aria-label="$t('CRM.previous')"
            :title="$t('CRM.previous')"
            icon="i-lucide-chevron-left"
            slate
            outline
            sm
            @click="page--"
          />
          <Button
            :disabled="page * 30 >= total || busy"
            :aria-label="$t('CRM.next')"
            :title="$t('CRM.next')"
            icon="i-lucide-chevron-right"
            slate
            outline
            sm
            @click="page++"
          />
        </footer>
      </aside>
      <div
        :class="{ 'hidden lg:flex': showContacts }"
        class="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-n-strong bg-n-solid-2"
      >
        <div
          class="flex items-center justify-between gap-3 border-b border-n-weak px-4 py-3"
        >
          <h2 class="m-0 flex items-center gap-2 text-sm font-semibold">
            <Icon icon="i-lucide-columns-3" class="size-4" />{{
              $t('CRM.board.pipeline')
            }}
          </h2>
          <div
            :title="$t('CRM.board.dragHelp')"
            class="text-xs text-n-slate-11"
          >
            <Icon icon="i-lucide-grip-vertical" class="size-4" />
          </div>
        </div>
        <main
          :class="{ 'hidden lg:flex': showContacts }"
          :aria-busy="loading"
          class="flex min-w-0 flex-1 items-stretch gap-3 overflow-auto p-4"
        >
          <CrmLoadingSkeleton v-if="loading" :columns="funnel?.columns || []" />
          <template v-else>
            <section
              v-for="column in funnel?.columns || []"
              :key="column.id"
              class="w-72 shrink-0 rounded-xl border border-n-weak bg-n-slate-2 dark:bg-n-solid-2"
              :class="{
                'outline outline-2 outline-n-brand': dropTarget === column.id,
              }"
              @dragover.prevent="dragging && (dropTarget = column.id)"
              @dragleave.self="dropTarget = ''"
              @drop.prevent="drop(column)"
            >
              <h3
                class="m-0 flex items-center gap-2 border-b border-n-weak px-3 py-3 text-sm font-semibold"
              >
                <span
                  aria-hidden="true"
                  class="i-lucide-columns-3 size-4 shrink-0 text-n-slate-11"
                /><span class="min-w-0 flex-1 truncate" :title="column.name">{{
                  column.name
                }}</span
                ><span
                  class="text-xs font-medium tabular-nums text-n-slate-11"
                  >{{ stageData(column)?.total || 0 }}</span
                >
              </h3>
              <div class="flex flex-col gap-2 p-2">
                <button
                  v-for="item in cards(column)"
                  :key="item.id"
                  class="group flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-3 dark:bg-n-solid-3 text-left hover:border-n-slate-7 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                  :draggable="!!canDrag(item)"
                  :class="{
                    'cursor-grab active:cursor-grabbing': canDrag(item),
                    'outline outline-2 outline-n-brand':
                      details?.id === item.id,
                  }"
                  @dragstart="startDrag($event, item)"
                  @dragend="endDrag"
                  @click="open(item, '', $event.currentTarget)"
                >
                  <span class="flex min-w-0 items-center gap-2.5">
                    <span
                      aria-hidden="true"
                      class="flex size-9 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-xs font-medium text-n-slate-11"
                      >{{ initials(item) }}
                      <Icon
                        v-if="!initials(item)"
                        icon="i-lucide-user-round"
                        class="size-4"
                    /></span>
                    <strong
                      class="min-w-0 flex-1 break-words text-sm font-medium"
                      >{{ contactName(item) }}</strong
                    ><span
                      aria-hidden="true"
                      class="i-lucide-chevron-right size-4 shrink-0 text-n-slate-10 group-hover:text-n-slate-12"
                    />
                  </span>
                  <span v-if="item.labels.length" class="flex flex-wrap gap-1">
                    <Label
                      v-for="label in item.labels.slice(0, 3)"
                      :key="label"
                      :title="label"
                      :color="labelColor(label)"
                      variant="smooth"
                      small
                      class="!m-0 max-w-full"
                    />
                    <span
                      v-if="item.labels.length > 3"
                      :title="item.labels.slice(3).join(', ')"
                      class="text-xs text-n-slate-11"
                      >+{{ item.labels.length - 3 }}</span
                    >
                  </span>
                  <span
                    class="flex items-start gap-2 text-sm"
                    :class="
                      item.workflow.state.review_required
                        ? 'text-n-amber-11'
                        : 'text-n-slate-11'
                    "
                  >
                    <span
                      aria-hidden="true"
                      class="mt-0.5 size-4 shrink-0"
                      :class="
                        item.workflow.state.status === 'closed'
                          ? 'i-lucide-archive'
                          : item.workflow.state.review_required
                            ? 'i-lucide-circle-alert'
                            : item.next_action
                              ? 'i-lucide-list-todo'
                              : 'i-lucide-circle-check'
                      "
                    />
                    <span class="line-clamp-2">{{
                      item.workflow.state.status === 'closed'
                        ? $t('CRM.closed')
                        : item.workflow.state.review_required
                          ? $t('CRM.review')
                          : item.next_action || $t('CRM.ready')
                    }}</span>
                  </span>
                  <span
                    v-if="item.workflow.deadline_at"
                    :title="`${$t('CRM.deadline')}: ${date(item.workflow.deadline_at)}`"
                    class="flex items-center gap-2 text-xs"
                    :class="
                      overdue(item) ? 'text-n-ruby-11' : 'text-n-slate-11'
                    "
                    ><span
                      aria-hidden="true"
                      class="size-4 shrink-0"
                      :class="
                        overdue(item)
                          ? 'i-lucide-alarm-clock'
                          : 'i-lucide-calendar-clock'
                      "
                    /><span
                      >{{ $t('CRM.deadline') }}:
                      {{ compactDate(item.workflow.deadline_at) }}</span
                    ></span
                  >
                  <span
                    class="flex items-center gap-2 border-t border-n-weak pt-2 text-xs text-n-slate-11"
                    :title="`${$t('CRM.responsible')}: ${item.assignee || $t('CRM.unassigned')}`"
                    ><span
                      aria-hidden="true"
                      class="size-4 shrink-0"
                      :class="
                        item.assignee
                          ? 'i-lucide-user-round'
                          : 'i-lucide-user-round'
                      "
                    /><span class="min-w-0 truncate"
                      >{{ item.assignee || $t('CRM.unassignedShort')
                      }}<template v-if="item.team">
                        · {{ item.team }}</template
                      ></span
                    ></span
                  >
                </button>
                <p
                  v-if="!cards(column).length"
                  class="m-0 flex items-center justify-center gap-2 px-2 py-6 text-xs text-n-slate-11"
                >
                  <span
                    aria-hidden="true"
                    class="i-lucide-inbox size-4 shrink-0"
                  />{{ $t('CRM.emptyPage') }}
                </p>
              </div>
              <div
                v-if="stageData(column)?.total > 30"
                class="flex items-center justify-between gap-2 border-t border-n-weak p-2"
              >
                <Button
                  :disabled="(stagePages[column.id] || 1) <= 1 || busy"
                  :aria-label="$t('CRM.previous')"
                  :title="$t('CRM.previous')"
                  icon="i-lucide-chevron-left"
                  slate
                  ghost
                  sm
                  @click="movePage(column, -1)"
                /><span class="text-xs tabular-nums">{{
                  stagePages[column.id] || 1
                }}</span
                ><Button
                  :disabled="
                    (stagePages[column.id] || 1) * 30 >=
                      stageData(column).total || busy
                  "
                  :aria-label="$t('CRM.next')"
                  :title="$t('CRM.next')"
                  icon="i-lucide-chevron-right"
                  slate
                  ghost
                  sm
                  @click="movePage(column, 1)"
                />
              </div>
            </section>
          </template>
        </main>
      </div>
    </div>
    <aside
      v-if="configuring && funnel"
      ref="settingsPanel"
      tabindex="-1"
      :aria-label="$t('CRM.settings.title')"
      class="absolute inset-y-0 right-0 z-20 w-full max-w-md overflow-hidden border-l border-n-strong bg-n-solid-2 shadow-xl focus:outline-none"
    >
      <CrmStageSettings
        :key="funnel.id"
        :funnel="funnel"
        contained
        @cancel="closeSettings"
        @saved="configured"
      />
    </aside>
    <aside
      v-if="details"
      ref="detailPanel"
      @keydown.esc="closeDetails"
      tabindex="-1"
      :aria-label="$t('CRM.board.details')"
      class="absolute inset-y-0 right-0 z-20 flex w-full max-w-sm flex-col border-l border-n-strong bg-n-solid-2 shadow-xl focus:outline-none"
    >
      <header class="flex items-start gap-3 border-b border-n-weak p-4">
        <div class="min-w-0 flex-1">
          <p class="mb-1 text-xs text-n-slate-11">
            {{ $t('CRM.board.details') }}
          </p>
          <h2 class="m-0 break-words text-sm font-semibold">
            {{ contactName(details) }}
          </h2>
          <p
            v-if="details.phone_number"
            class="mb-0 mt-1 text-xs text-n-slate-11"
          >
            {{ details.phone_number }}
          </p>
        </div>
        <Button
          icon="i-lucide-x"
          :aria-label="$t('CRM.closePanel')"
          :title="$t('CRM.closePanel')"
          slate
          ghost
          sm
          @click="closeDetails()"
        />
      </header>
      <div class="min-h-0 flex-1 overflow-auto">
        <div
          v-if="details.labels.length || details.last_message"
          class="border-b border-n-weak p-4"
        >
          <div class="mb-3 flex flex-wrap gap-1">
            <Label
              v-for="label in details.labels"
              :key="label"
              :title="label"
              :color="labelColor(label)"
              variant="smooth"
              small
            />
          </div>
          <p v-if="details.last_message" class="mb-1 text-xs text-n-slate-11">
            {{ $t('CRM.board.lastMessage') }}
          </p>
          <p class="m-0 break-words text-sm">{{ details.last_message }}</p>
        </div>
        <CrmPanel
          v-if="details.conversation_id"
          :key="details.id"
          :conversation-id="details.conversation_id"
          :funnel-id="selected"
          :requested-stage="requestedStage"
          @move-handled="requestedStage = ''"
          @changed="load"
        />
        <p v-else class="p-4 text-sm text-n-slate-11">
          {{ $t('CRM.board.noConversation') }}
        </p>
      </div>
      <footer class="border-t border-n-weak p-3">
        <Button
          :label="
            details.conversation_id
              ? $t('CRM.board.openConversation')
              : $t('CRM.board.openContact')
          "
          icon="i-lucide-external-link"
          slate
          outline
          class="w-full"
          @click="openConversation(details)"
        />
      </footer>
    </aside>
  </div>
</template>
